import { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import Pagination from "@mui/material/Pagination";
import Flatpickr from "react-flatpickr";
import { authContext } from "../contexts/AuthProvider";
import { loaderContext } from "../contexts/LoaderProvider";
import { branchSwitchContext } from "../contexts/BranchSwitchProvider";
import Loader from "../layout/Loader";
import Header from "../layout/Header";
import Sidebar from "../layout/Sidebar";
import Footer from "../layout/Footer";
import ScrollToTop from "../layout/ScrollToTop";
import LoadScript from "../utils/LoadScript";
import {
  orderHistory,
  getOrderDetails,
  createNewOrder,
  getOrderRawData,
} from "../services/orderService";
import OrderDetailsPopup from "./OrderDetailsPopup";
import ExportOrderHistory from "./ExportOrderHistory";
import Confirmation from "../layout/Confirmation";
import ViewDetails from "../layout/button/order-history/ViewDetails";
import Reorder from "../layout/button/order/Reorder";
import socket from "../socket/socket";
import { calculateDeliveryFee } from "../services/locationService";
import ErrorPopup from "../layout/ErrorPopup";
import { formatDecimal } from "../utils/decimalFormat";

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [details, setDetails] = useState({});
  const [popup, setPopup] = useState(false);
  const [reorderPopup, setReorderPopup] = useState({ open: false, data: null });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const { isLoading, setIsLoading } = useContext(loaderContext);
  const { branchSwitch } = useContext(branchSwitchContext);
  const [isAuth] = useContext(authContext);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const limit = 6;

  const getOrders = useCallback(async () => {
    setIsLoading(true);
    let startDate = dateRange[0]
      ? moment(dateRange[0]).format("YYYY-MM-DD")
      : null;
    let endDate = dateRange[1]
      ? moment(dateRange[1]).format("YYYY-MM-DD")
      : null;
    const response = await orderHistory(
      page,
      limit,
      startDate,
      endDate,
      search,
    );
    if (response.data.code === 1) {
      setOrders(response.data.data);
      setTotalPages(response.data.pages);
      setIsLoading(false);
    }
  }, [page, search, limit, setIsLoading, dateRange]);

  const showOrderDetails = useCallback(async (orderId) => {
    const response = await getOrderDetails(orderId);
    if (response?.data?.code === 1) {
      setDetails(response?.data?.data);
      setPopup(true);
    }
  }, []);

  const reOrder = useCallback(async () => {
    if (!reorderPopup.data) return;
    setBusy(true);
    const order = await getOrderRawData(reorderPopup?.data?._id);
    const orderData = order?.data?.data;

    const endLatitude = orderData?.street?.latitude;
    const endLongitude = orderData?.street?.longitude;

    if (endLatitude && endLongitude) {
      const response = await calculateDeliveryFee(endLatitude, endLongitude);
      if (response.data.code === 1) {
        const time = response?.data?.data?.duration;
        const distance = response?.data?.data?.distance;
        const feeInt = response?.data?.data?.feeInt ?? 0;

        const result = await createNewOrder({
          ...orderData,
          status: "0",
          time: time,
          distance: distance,
          feeInt: feeInt,
        });
        if (result.data.code === 1) {
          setBusy(false);
          socket.emit("merchant:sendOrderRequest", result?.data?.data);
          navigate("/dashboard");
        } else {
          setReorderPopup({ open: false, data: null });
          setBusy(false);
          setError(result?.data?.message);
        }
      } else {
        setReorderPopup({ open: false, data: null });
        setBusy(false);
        setError("Unable to fetch order data. Please try again later.");
      }
    } else {
      setReorderPopup({ open: false, data: null });
      setBusy(false);
      setError("Unable to fetch order data. Please try again later.");
    }
  }, [reorderPopup.data, navigate]);

  const resetFilter = () => {
    setPage(1);
    setSearch("");
    setDateRange([null, null]);
  };

  useEffect(() => {
    if (isAuth === true) {
      setIsLoading(false);
    }
    if (isAuth === false) {
      navigate("/");
    }
  }, [isAuth, navigate, setIsLoading]);

  useEffect(() => {
    getOrders();
  }, [page, search, getOrders, branchSwitch]);

  const orderStatus = (status) => {
    switch (status) {
      case "6":
        return <div className="badge bg-success-transparent">Completed</div>;
      case "-2":
      case "-3":
        return <div className="badge bg-danger-transparent">Cancelled</div>;
      default:
        return <div></div>;
    }
  };

  const renderOrderList = (orders) => (
    <>
      {isLoading === true ? (
        <Loader />
      ) : (
        <>
          {orders?.length > 0 ? (
            <div className="table-responsive">
              <table className="table text-nowrap">
                <thead>
                  <tr>
                    <th scope="col">Order Date</th>
                    <th scope="col">Order Status</th>
                    <th scope="col">Order Id</th>
                    <th scope="col">Driver</th>
                    <th scope="col">Customer</th>
                    <th scope="col">Order Amount</th>
                    <th scope="col">Fee</th>
                    <th scope="col">Payment Type</th>
                    <th scope="col">Transaction Id</th>
                    <th scope="col">Vehicle Type</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders?.map((order, index) => (
                    <tr key={index}>
                      <td>
                        {moment(order?.orderDateTime)
                          .tz(process.env.REACT_APP_TIMEZONE)
                          .format("Do MMM, YYYY")}
                      </td>
                      <td>{orderStatus(order?.status)}</td>
                      <td>{order?.orderId}</td>
                      <td>
                        {order?.driverName ? (
                          <div className="d-flex align-items-center gap-2">
                            <div className="lh-1">
                              <span className="avatar avatar-md avatar-rounded">
                                <img
                                  src={`${process.env.REACT_APP_API_PUBLIC_URL}${order?.driverPhoto}`}
                                  alt={order?.driverName}
                                />
                              </span>
                            </div>
                            <div>
                              <p className="fw-medium fs-13 mb-0 d-flex align-items-center">
                                {order?.driverName}
                              </p>
                            </div>
                          </div>
                        ) : (
                          "---"
                        )}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div>
                            <p className="fw-medium fs-13 mb-0 d-flex align-items-center">
                              {order?.customerName}
                            </p>
                            <p className="fs-12 text-muted mb-0">
                              {order?.customerPhone}
                            </p>
                            <p className="fs-12 text-muted mb-0">
                              {order?.area}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>{order?.orderAmount}</td>
                      <td>
                        <div className="badge bg-success-transparent">
                          {formatDecimal(order?.feeInt)} KWD
                        </div>
                      </td>
                      <td>{order?.paymentType}</td>
                      <td>{order?.transactionId ?? "---"}</td>
                      <td>{order?.vehicleType}</td>
                      <td>
                        <div className="btn-list">
                          <ViewDetails
                            showOrderDetails={showOrderDetails}
                            order={order}
                          />
                          <Reorder
                            setReorderPopup={setReorderPopup}
                            order={order}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="11">
                      <div className="d-flex justify-content-end">
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, value) => setPage(value)}
                        />
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center my-5">
              <img
                className="mx-auto w-25 my-5"
                src={`${process.env.REACT_APP_PUBLIC_URL}assets/images/media/backgrounds/no-content.png`}
                alt="no-content"
              />
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <div className="page">
        <Header />
        <Sidebar />
        <div className="main-content app-content">
          <div className="container-fluid">
            <div className="row mt-4">
              <div className="col-md-12">
                <div className="card custom-card">
                  <div className="card-header justify-content-between">
                    <div className="card-title">
                      <div className="d-flex justify-content-between align-items-center px-3 py- gap-2">
                        <span className="d-block fw-medium fs-15">
                          Order History
                        </span>
                      </div>
                    </div>
                    <div className="d-block d-md-flex flex-wrap gap-2">
                      <div>
                        <input
                          className="form-control form-control-md mb-2"
                          type="text"
                          placeholder="Search Here"
                          aria-label=".form-control-sm example"
                          onChange={(e) => setSearch(e.target.value)}
                          value={search}
                        />
                      </div>
                      <div>
                        <Flatpickr
                          className="form-control form-control-md mb-2"
                          placeholder="Search by date range"
                          options={{ mode: "range", dateFormat: "d-m-Y" }}
                          value={dateRange}
                          onChange={(dates) => {
                            if (dates.length === 2) {
                              setDateRange(dates);
                            }
                          }}
                        />
                      </div>
                      <div>
                        <button
                          onClick={resetFilter}
                          className="btn btn-danger btn-md w-100 w-md-auto mb-2"
                        >
                          <i className="ri-tools-line me-1 fw-medium align-middle"></i>
                          Reset
                        </button>
                      </div>
                      <div>
                        <ExportOrderHistory
                          search={search}
                          dateRange={dateRange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-0">{renderOrderList(orders)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
      <OrderDetailsPopup order={details} popup={popup} setPopup={setPopup} />
      <Confirmation
        popup={reorderPopup}
        setPopup={setReorderPopup}
        text={`REORDER`}
        action={reOrder}
        busy={busy}
      />
      <ErrorPopup error={error} setError={setError} />
      <ScrollToTop />
      <LoadScript />
    </>
  );
};

export default OrderHistory;
